import { RootState } from "store";
import { StateCreator } from "zustand";

import { updateBrowserStatus } from "common/MetaActions";

import { Thread } from "models/inbox/inbox";
import { UploadedDocument } from "models/dashboard/dashboard";
import { MessagesAPI } from "api/messages";
import { FilesAPI } from "api/files";
import { ignoreStaleResponse } from "helpers/ignoreStaleResponse";

export interface StatusSlice {
  unreadThreads: number;
  unhandledUploads: number;

  setUnreadThreadsStatus: (customerId: string) => Promise<Thread[] | undefined>;
  setUnhandledUploadsStatus: (
    customerId: string
  ) => Promise<UploadedDocument[] | undefined>;
  resetStatus: () => void;
}

export const createStatusSlice: StateCreator<
  RootState,
  [["zustand/devtools", never]],
  [],
  StatusSlice
> = (set, get) => ({
  unreadThreads: 0,
  unhandledUploads: 0,

  setUnreadThreadsStatus: async (customerId) => {
    try {
      const unreadThreads = await MessagesAPI.fetchThreads(customerId);
      updateBrowserStatus({
        unreadThreads: unreadThreads.body.length,
        unhandledUploads: get().status.unhandledUploads
      });
      const activeId = get().global.workingCustomerId;
      if (ignoreStaleResponse(activeId, customerId, "setUnreadThreadsStatus")) {
        return;
      }
      set(
        ({ status }) => ({
          status: {
            ...status,
            unreadThreads: unreadThreads.body.length
          }
        }),
        false,
        "status/SET_UNREAD_THREADS_STATUS"
      );
      return unreadThreads.body as Thread[];
    } catch (error) {
      get().error.appendErrorNotification(
        "Hämtning av olästa meddelanden misslyckades.",
        error
      );
    }
  },

  setUnhandledUploadsStatus: async (customerId) => {
    try {
      const unhandledUploads = await FilesAPI.fetchDocuments(customerId);
      updateBrowserStatus({
        unreadThreads: get().status.unreadThreads,
        unhandledUploads: unhandledUploads.body.length
      });
      const activeId = get().global.workingCustomerId;
      if (
        ignoreStaleResponse(activeId, customerId, "setUnhandledUploadsStatus")
      ) {
        return;
      }
      set(
        ({ status }) => ({
          status: {
            ...status,
            unhandledUploads: unhandledUploads.body.length
          }
        }),
        false,
        "status/SET_UNHANDLED_UPLOADS_STATUS"
      );
      return unhandledUploads.body as UploadedDocument[];
    } catch (error) {
      get().error.appendErrorNotification(
        "Hämtning av nya inleveranser misslyckades.",
        error
      );
    }
  },

  resetStatus: () => {
    updateBrowserStatus({
      unreadThreads: 0,
      unhandledUploads: 0
    });
    set(
      ({ status }) => ({
        status: {
          ...status,
          unreadThreads: 0,
          unhandledUploads: 0
        }
      }),
      false,
      "status/RESET_STATUS"
    );
  }
});
